import {
  CheckOutlined,
  EditOutlined,
  RollbackOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { PRIORITY_COLOR, STATUS_COLOR_CLASS } from './constants';
import s from './styles.module.scss';
import { canEditTask } from './types';

import type { TFunction } from 'i18next';
import type { FC } from 'react';
import type { DisplayStatus, ITaskCard } from './types';

export interface ITaskItemProps {
  task: ITaskCard;
  currentUserGuid: string;
  isCompleting?: boolean;
  isAssigning?: boolean;
  onComplete?: () => void;
  onReturn?: () => void;
  onAssign?: () => void;
  onEdit?: () => void;
}

const MAX_DISPLAY_LENGTH = 100;

const truncateText = (text: string, maxLength = MAX_DISPLAY_LENGTH): string =>
  text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

const PRIORITY_LABEL_KEY = {
  high: 'tasksDrawer.priorityHigh',
  medium: 'tasksDrawer.priorityMedium',
  low: 'tasksDrawer.priorityLow',
} as const;

const getDisplayStatus = (
  task: ITaskCard,
  currentUserGuid: string,
): DisplayStatus => {
  if (task.status === 'completed') return 'completed';
  if (task.status === 'unassigned') return 'open';
  if (
    currentUserGuid &&
    task.status === 'assigned' &&
    task.assignedToGuid === currentUserGuid
  )
    return 'assigned_to_me';
  return 'assigned_to_other';
};

const getStatusLabel = (
  displayStatus: DisplayStatus,
  assigneeName: string | null | undefined,
  t: TFunction,
  completedByName?: string | null,
  completedAt?: string | null,
): string => {
  if (displayStatus === 'completed') {
    if (completedByName) {
      const date = completedAt
        ? ` ${dayjs(completedAt).format('MM/DD/YYYY')}`
        : '';
      return t('tasksDrawer.statusCompletedBy', {
        name: `${completedByName}${date}`,
      }).trim();
    }
    return t('tasksDrawer.statusCompleted');
  }
  if (displayStatus === 'open') return t('tasksDrawer.statusOpen');
  if (displayStatus === 'assigned_to_me')
    return t('tasksDrawer.statusAssignedToMe');
  return assigneeName
    ? t('tasksDrawer.statusAssignedTo', { name: assigneeName })
    : t('tasksDrawer.statusAssigned');
};

const formatDate = (dateStr: string): string => {
  const d = dayjs(dateStr);
  return d.isValid() ? d.format('MM/DD/YYYY, h:mm A') : '—';
};

const TaskItem: FC<ITaskItemProps> = ({
  task,
  currentUserGuid,
  isCompleting = false,
  isAssigning = false,
  onComplete,
  onReturn,
  onAssign,
  onEdit,
}) => {
  const { t } = useTranslation();
  const displayStatus = getDisplayStatus(task, currentUserGuid);
  const statusColorClass = STATUS_COLOR_CLASS[displayStatus];
  const isReportedByMe = task.reportedByGuid === currentUserGuid;
  const canEdit = canEditTask(task, currentUserGuid) && Boolean(onEdit);

  const renderEditButton = () => {
    if (!canEdit) return null;
    return (
      <Tooltip
        title={t('tasksDrawer.edit')}
        trigger={['hover', 'focus']}
        placement="top"
      >
        <Button
          className={s['action-button']}
          icon={<EditOutlined />}
          onClick={onEdit}
          size="small"
          disabled={isCompleting || isAssigning}
          aria-label={t('tasksDrawer.edit')}
          data-cy="edit-task-button"
        />
      </Tooltip>
    );
  };

  const renderActions = () => {
    if (displayStatus === 'completed') return null;

    if (displayStatus === 'assigned_to_me') {
      return (
        <div className={s['task-actions']}>
          {renderEditButton()}
          <Tooltip
            title={t('tasksDrawer.complete')}
            trigger={['hover', 'focus']}
            placement="top"
          >
            <span
              className={classNames({
                [s['action-wrapper--disabled']]: isCompleting || !onComplete,
              })}
            >
              <Button
                className={s['complete-button']}
                type="primary"
                icon={<CheckOutlined />}
                onClick={onComplete}
                size="small"
                loading={isCompleting}
                disabled={isCompleting || !onComplete}
                aria-label={t('tasksDrawer.complete')}
                data-cy="complete-task-button"
              />
            </span>
          </Tooltip>
          <Tooltip
            title={t('tasksDrawer.returnToPool')}
            trigger={['hover', 'focus']}
            placement="top"
          >
            <span
              className={classNames({
                [s['action-wrapper--disabled']]: isCompleting || !onReturn,
              })}
            >
              <Button
                className={s['action-button']}
                icon={<RollbackOutlined />}
                onClick={onReturn}
                size="small"
                disabled={isCompleting || !onReturn}
                aria-label={t('tasksDrawer.returnToPool')}
                data-cy="return-task-button"
              />
            </span>
          </Tooltip>
        </div>
      );
    }

    if (displayStatus === 'assigned_to_other') {
      return null;
    }

    return (
      <div className={s['task-actions']}>
        {renderEditButton()}
        <Tooltip
          title={t('tasksDrawer.assignToMe')}
          trigger={['hover', 'focus']}
          placement="top"
        >
          <span
            className={classNames({
              [s['action-wrapper--disabled']]: isAssigning || !onAssign,
            })}
          >
            <Button
              className={s['action-button']}
              icon={<UserAddOutlined />}
              onClick={onAssign}
              size="small"
              loading={isAssigning}
              disabled={isAssigning || !onAssign}
              aria-label={t('tasksDrawer.assignToMe')}
              data-cy="assign-task-button"
            />
          </span>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className={s['task-item']}>
      <div className={s['task-header']}>
        <div className={s['task-title-row']}>
          <p className={s['task-title']}>{task.title}</p>
          <Tag color={PRIORITY_COLOR[task.priority]}>
            {t(PRIORITY_LABEL_KEY[task.priority])}
          </Tag>
        </div>
        {renderActions()}
      </div>

      <div className={s['status-row']}>
        <span className={classNames(s['status-dot'], s[statusColorClass])} />
        <p className={classNames(s['status-label'], s[statusColorClass])}>
          {getStatusLabel(
            displayStatus,
            task.assignedToName,
            t,
            task.completedByName,
            task.completedAt,
          )}
        </p>
      </div>

      <div className={s['reporter-row']}>
        <p className={s['reporter-label']}>{t('tasksDrawer.reportedBy')}</p>
        <p className={s['reporter-name']}>
          {task.reportedByName ?? t('tasksDrawer.unknown')}
          {isReportedByMe && (
            <span className={s['reporter-me']}>
              {` `}
              {t('tasksDrawer.me')}
            </span>
          )}
        </p>
        <p className={s['reporter-date']}>{formatDate(task.createdAt)}</p>
      </div>

      {task.quickNotes && (
        <div className={s['return-note']}>
          <p className={s['return-note-label']}>
            {t('tasksDrawer.return')}{' '}
            <span className={s['return-note-text']}>
              &ldquo;{truncateText(task.quickNotes)}&rdquo;
            </span>
          </p>
          {task.updatedAt && (
            <p className={s['return-note-date']}>
              {formatDate(task.updatedAt)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskItem;
